import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Send, RefreshCw, Search, Upload, Loader2, Building2, User, ShieldCheck, CalendarClock, MessageSquare, UserCheck, UserPlus, MessageCircle, Smile, Meh, Frown, Brain } from 'lucide-react';

const PRIORITA = {
  calda: { label: 'Calda', cls: 'bg-red-100 text-red-700 border-red-200', icon: '🔥' },
  media: { label: 'Media', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: '🟡' },
  fredda: { label: 'Fredda', cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: '⚪' },
};
const ESITI = [
  { v: 'nuova', l: 'Nuova', c: 'bg-blue-100 text-blue-700' },
  { v: 'da_richiamare', l: 'Da richiamare', c: 'bg-violet-100 text-violet-700' },
  { v: 'interessato', l: 'Interessato', c: 'bg-emerald-100 text-emerald-700' },
  { v: 'venduto', l: 'Venduto', c: 'bg-green-600 text-white' },
  { v: 'non_interessato', l: 'Non interessato', c: 'bg-slate-200 text-slate-600' },
  { v: 'scartata', l: 'Scartata', c: 'bg-slate-100 text-slate-400' },
];
const PRODOTTI = ['Casa', 'Infortuni/Famiglia', 'Vita', 'Salute', 'RC Professionale', 'Prestiti/Finanziamenti'];
const esitoMeta = (v) => ESITI.find((e) => e.v === v) || ESITI[0];

const SENT = {
  interessato: { l: 'Interessato', c: 'bg-emerald-100 text-emerald-700', Icon: Smile },
  non_interessato: { l: 'Non interessato', c: 'bg-red-100 text-red-600', Icon: Frown },
  incerto: { l: 'Incerto', c: 'bg-amber-100 text-amber-700', Icon: Meh },
  nessuna_risposta: { l: 'Nessuna risposta', c: 'bg-slate-100 text-slate-500', Icon: Meh },
};
const SENT_MSG = {
  positivo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  negativo: 'bg-red-50 text-red-600 border-red-200',
  domanda: 'bg-sky-50 text-sky-700 border-sky-200',
  neutro: 'bg-slate-50 text-slate-500 border-slate-200',
};

const VISTE = [
  { v: 'oggi', l: 'Da lavorare oggi' },
  { v: 'calde', l: 'Calde' },
  { v: 'nuovi', l: 'Nuovi' },
  { v: 'tutte', l: 'Tutte' },
];

export default function CrossSell() {
  const [vista, setVista] = useState('oggi');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoPersona, setTipoPersona] = useState('tutti');
  const [agente, setAgente] = useState('tutti');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');
  const [prodScelto, setProdScelto] = useState('');
  const [showSondaggio, setShowSondaggio] = useState(false);
  const [templates, setTemplates] = useState({ fisica: '', giuridica: '' });
  const [preview, setPreview] = useState(null);
  const [campagna, setCampagna] = useState(null);
  const [esitoFilter, setEsitoFilter] = useState('');
  const [soloMie, setSoloMie] = useState(false);
  const [conv, setConv] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get('/crosssell/stats'); setStats(data); } catch (e) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { vista, limit: 150 };
      if (search) params.search = search;
      if (tipoPersona !== 'tutti') params.tipo_persona = tipoPersona;
      if (agente !== 'tutti') params.agente = agente;
      if (esitoFilter) params.esito = esitoFilter;
      if (soloMie) params.preso_in_carico = 'me';
      const { data } = await api.get('/crosssell/opportunita', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Errore nel caricamento opportunità');
    } finally { setLoading(false); }
  }, [vista, search, tipoPersona, agente, esitoFilter, soloMie]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const seed = async () => {
    setBusy('seed');
    try {
      const { data } = await api.post('/crosssell/seed');
      toast.success(`Base clienti aggiornata: ${data.totale} clienti (${data.created} nuovi)`);
      await load(); await loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); } finally { setBusy(''); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy('import');
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await api.post('/crosssell/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import: ${data.nuovi} nuovi · ${data.updated} aggiornati · ${data.righe_uniche} clienti unici${data.saltate ? ` · ${data.saltate} righe saltate` : ''}`);
      await load(); await loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import fallito'); }
    finally { setBusy(''); e.target.value = ''; }
  };

  const openSondaggio = async () => {
    setShowSondaggio(true);
    try {
      const { data } = await api.get('/crosssell/sondaggio-template');
      setTemplates(data);
    } catch { /* noop */ }
    const filtro = { vista, esito: '', tipo_persona: tipoPersona !== 'tutti' ? tipoPersona : '', agente: agente !== 'tutti' ? agente : '', search };
    try {
      const { data } = await api.post('/crosssell/sondaggio/preview', { filtro, solo_mai_contattati: true });
      setPreview(data);
    } catch { setPreview(null); }
    try { const { data } = await api.get('/crosssell/sondaggio/stato'); setCampagna(data && data.id ? data : null); } catch { /* noop */ }
  };

  const salvaTemplate = async () => {
    setBusy('tpl');
    try { await api.put('/crosssell/sondaggio-template', templates); toast.success('Template salvato'); }
    catch { toast.error('Errore salvataggio'); } finally { setBusy(''); }
  };

  const avviaCampagna = async () => {
    setBusy('campagna');
    const filtro = { vista, esito: '', tipo_persona: tipoPersona !== 'tutti' ? tipoPersona : '', agente: agente !== 'tutti' ? agente : '', search };
    try {
      const { data } = await api.post('/crosssell/sondaggio/invia-blocco', { filtro, solo_mai_contattati: true });
      toast.success(`Campagna avviata: ${data.totale} invii (~${data.eta_minuti} min)`);
      pollCampagna();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore avvio campagna'); }
    finally { setBusy(''); }
  };

  const pollCampagna = useCallback(() => {
    const tick = async () => {
      try {
        const { data } = await api.get('/crosssell/sondaggio/stato');
        setCampagna(data && data.id ? data : null);
        if (data && data.stato === 'in_corso') setTimeout(tick, 5000);
        else { loadStats(); load(); }
      } catch { /* noop */ }
    };
    tick();
  }, [loadStats, load]);

  const openCard = async (it) => {
    setProdScelto(''); setConv(null);
    try { const { data } = await api.get(`/crosssell/${it.id}`); setSelected(data); }
    catch { setSelected(it); }
    try { const { data } = await api.get(`/crosssell/${it.id}/conversazione`); setConv(data); } catch { /* noop */ }
  };

  const patch = async (cid, body, silent) => {
    try {
      const { data } = await api.patch(`/crosssell/${cid}`, body);
      setSelected(data);
      setItems((prev) => prev.map((x) => (x.id === cid ? data : x)));
      if (!silent) toast.success('Aggiornato');
      loadStats();
      return data;
    } catch (e) { toast.error('Errore aggiornamento'); }
  };

  const generaAI = async (cid, prodotto) => {
    setBusy('ai');
    try {
      const { data } = await api.post(`/crosssell/${cid}/genera-ai`, { prodotto: prodotto || null }, { timeout: 120000 });
      setSelected(data);
      toast.success('Proposta AI generata');
    } catch (e) {
      const msg = e?.code === 'ECONNABORTED'
        ? 'La generazione sta impiegando troppo tempo, riprova tra poco'
        : (e?.response?.data?.detail || 'Generazione AI fallita');
      toast.error(msg);
    }
    finally { setBusy(''); }
  };

  const verificaSintesi = async (cid) => {
    setBusy('sintesi');
    try {
      const { data } = await api.post(`/crosssell/${cid}/verifica-sintesi`, {}, { timeout: 120000 });
      setSelected((s) => ({ ...s, sintesi_polizze: data.sintesi_polizze, sintesi_checked_at: new Date().toISOString() }));
      toast.success('Polizze Sintesi aggiornate');
    } catch (e) {
      const msg = e?.code === 'ECONNABORTED'
        ? 'La verifica Sintesi sta impiegando troppo tempo, riprova tra poco'
        : (e?.response?.data?.detail || 'Verifica Sintesi fallita');
      toast.error(msg);
    }
    finally { setBusy(''); }
  };

  const inviaWA = async (cid) => {
    setBusy('wa');
    try {
      await api.post(`/crosssell/${cid}/invia-wa`);
      toast.success('Messaggio WhatsApp inviato');
      await patch(cid, { esito: 'da_richiamare' }, true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invio WhatsApp fallito'); }
    finally { setBusy(''); }
  };

  const prendiCarico = async (cid) => {
    try {
      const { data } = await api.post(`/crosssell/${cid}/prendi-in-carico`);
      setSelected(data);
      setItems((prev) => prev.map((x) => (x.id === cid ? data : x)));
      toast.success('Preso in carico');
    } catch { toast.error('Errore'); }
  };

  const rilasciaCarico = async (cid) => {
    try {
      const { data } = await api.post(`/crosssell/${cid}/rilascia`);
      setSelected(data);
      setItems((prev) => prev.map((x) => (x.id === cid ? data : x)));
      toast.success('Rilasciato');
    } catch { toast.error('Errore'); }
  };

  const analizzaSentiment = async (cid) => {
    setBusy('sentiment-ai');
    try {
      const { data } = await api.post(`/crosssell/${cid}/analizza-sentiment`, {}, { timeout: 120000 });
      setConv({ messaggi: data.messaggi, sentiment: data.sentiment });
      setSelected((s) => ({ ...s, sentiment: data.sentiment, esito: data.esito }));
      setItems((prev) => prev.map((x) => (x.id === cid ? { ...x, sentiment: data.sentiment, esito: data.esito } : x)));
      if (data.esito_cambiato) toast.success('Cliente interessato! Esito aggiornato a "Interessato"');
      else toast.success('Analisi completata');
      loadStats();
    } catch (e) {
      const msg = e?.code === 'ECONNABORTED' ? 'Analisi troppo lunga, riprova' : (e?.response?.data?.detail || 'Analisi fallita');
      toast.error(msg);
    } finally { setBusy(''); }
  };

  const StatCard = ({ label, value, color, filterEsito }) => (
    <button type="button" disabled={!filterEsito}
      onClick={() => filterEsito && setEsitoFilter((f) => (f === filterEsito ? '' : filterEsito))}
      className={`flex-1 min-w-[120px] text-left bg-white rounded-xl border px-4 py-3 transition-all ${filterEsito ? 'cursor-pointer hover:border-slate-300 hover:shadow-sm' : 'cursor-default'} ${filterEsito && esitoFilter === filterEsito ? 'border-slate-900 ring-2 ring-slate-900/10' : 'border-slate-200'}`}
      data-testid={`stat-${label}`}>
      <div className={`text-2xl font-bold ${color || 'text-slate-800'}`}>{value ?? '–'}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="crosssell-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cross-sell / Opportunità</h1>
          <p className="text-sm text-slate-500">Vendi altre polizze ai clienti auto — l'AI suggerisce il prodotto e prepara il contatto.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openSondaggio} data-testid="sondaggio-btn">
            <MessageSquare size={15} className="mr-1" /> Sondaggio in blocco
          </Button>
          <label>
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onImport} data-testid="import-input" />
            <Button variant="outline" size="sm" asChild disabled={busy === 'import'}>
              <span className="cursor-pointer">{busy === 'import' ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} Importa clienti</span>
            </Button>
          </label>
          <Button size="sm" onClick={seed} disabled={busy === 'seed'} data-testid="seed-btn">
            {busy === 'seed' ? <Loader2 size={15} className="animate-spin mr-1" /> : <RefreshCw size={15} className="mr-1" />} Aggiorna da scadenze
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <StatCard label="Totale" value={stats?.totale} />
        <StatCard label="Calde" value={stats?.calde} color="text-red-600" />
        <StatCard label="Nuovi" value={stats?.nuovi} color="text-blue-600" />
        <StatCard label="Da richiamare" value={stats?.da_richiamare} color="text-violet-600" filterEsito="da_richiamare" />
        <StatCard label="Interessati" value={stats?.interessati} color="text-emerald-600" filterEsito="interessato" />
        <StatCard label="Venduti" value={stats?.venduti} color="text-green-600" filterEsito="venduto" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {VISTE.map((v) => (
            <button key={v.v} onClick={() => setVista(v.v)} data-testid={`vista-${v.v}`}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${vista === v.v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {v.l}
            </button>
          ))}
        </div>
        <button onClick={() => setSoloMie((m) => !m)} data-testid="filter-mie"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border font-medium transition-colors ${soloMie ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
          <UserCheck size={15} /> Le mie
        </button>
        {esitoFilter && (
          <button onClick={() => setEsitoFilter('')} data-testid="clear-esito-filter"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full bg-slate-900 text-white font-medium">
            {esitoMeta(esitoFilter).l} ✕
          </button>
        )}
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome, telefono, targa" className="pl-8 h-9 w-56" data-testid="search-input" />
        </div>
        <Select value={tipoPersona} onValueChange={setTipoPersona}>
          <SelectTrigger className="h-9 w-40" data-testid="filter-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i tipi</SelectItem>
            <SelectItem value="fisica">Persone fisiche</SelectItem>
            <SelectItem value="giuridica">Persone giuridiche</SelectItem>
          </SelectContent>
        </Select>
        {stats?.agenti?.length > 0 && (
          <Select value={agente} onValueChange={setAgente}>
            <SelectTrigger className="h-9 w-44" data-testid="filter-agente"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli agenti</SelectItem>
              {stats.agenti.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <span className="text-xs text-slate-500 ml-auto">{total} opportunità</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 size={28} className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-200">
          <p className="text-slate-500">Nessuna opportunità in questa vista.</p>
          <p className="text-sm text-slate-400 mt-1">Premi "Aggiorna da scadenze" per generare la base clienti.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((it) => {
            const p = PRIORITA[it.priorita] || PRIORITA.fredda;
            const em = esitoMeta(it.esito);
            return (
              <button key={it.id} onClick={() => openCard(it)} data-testid={`opp-card-${it.id}`}
                className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {it.tipo_persona === 'giuridica' ? <Building2 size={15} className="text-slate-400 shrink-0" /> : <User size={15} className="text-slate-400 shrink-0" />}
                    <span className="font-semibold text-slate-800 truncate">{it.nominativo}</span>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${p.cls}`}>{p.icon} {p.label}</span>
                </div>
                <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                  {it.targhe?.length > 0 && <div>Targhe: {it.targhe.join(', ')}</div>}
                  {it.compagnie?.length > 0 && <div>Compagnie: {it.compagnie.join(', ')}</div>}
                  {it.is_new && <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] mt-1">NUOVO</Badge>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${em.c}`}>{em.l}</span>
                  {it.prodotto_suggerito && <span className="text-[11px] text-indigo-600 font-medium flex items-center gap-1"><Sparkles size={11} />{it.prodotto_suggerito}</span>}
                </div>
                {(it.preso_in_carico_nome || it.sentiment?.interesse) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {it.preso_in_carico_nome && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center gap-1" data-testid={`card-owner-${it.id}`}>
                        <UserCheck size={10} /> {it.preso_in_carico_nome}
                      </span>
                    )}
                    {it.sentiment?.interesse && SENT[it.sentiment.interesse] && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${SENT[it.sentiment.interesse].c}`}>
                        {React.createElement(SENT[it.sentiment.interesse].Icon, { size: 10 })} {SENT[it.sentiment.interesse].l}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="opp-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {selected.tipo_persona === 'giuridica' ? <Building2 size={18} /> : <User size={18} />}
                  {selected.nominativo}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Telefono</span><span className="font-medium">{selected.cellulare || '—'}</span></div>
                  {selected.email && <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{selected.email}</span></div>}
                  {selected.targhe?.length > 0 && <div className="flex justify-between"><span className="text-slate-500">Targhe</span><span className="font-medium">{selected.targhe.join(', ')}</span></div>}
                  {selected.compagnie?.length > 0 && <div className="flex justify-between"><span className="text-slate-500">Compagnie</span><span className="font-medium">{selected.compagnie.join(', ')}</span></div>}
                  {selected.agente && <div className="flex justify-between"><span className="text-slate-500">Agente</span><span className="font-medium">{selected.agente}</span></div>}
                </div>

                <div className="flex items-center justify-between gap-2 bg-indigo-50/60 border border-indigo-100 rounded-lg p-2.5">
                  {selected.preso_in_carico_nome ? (
                    <>
                      <span className="text-xs text-indigo-700 flex items-center gap-1.5"><UserCheck size={14} /> In carico a <b>{selected.preso_in_carico_nome}</b></span>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => rilasciaCarico(selected.id)} data-testid="rilascia-btn">Rilascia</Button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-slate-500">Nessun operatore assegnato</span>
                      <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => prendiCarico(selected.id)} data-testid="prendi-carico-btn"><UserPlus size={13} className="mr-1" /> Prendi in carico</Button>
                    </>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-500 uppercase">Polizze su Sintesi</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === 'sintesi'} onClick={() => verificaSintesi(selected.id)} data-testid="verifica-sintesi-btn">
                      {busy === 'sintesi' ? <Loader2 size={13} className="animate-spin mr-1" /> : <ShieldCheck size={13} className="mr-1" />} Verifica su Sintesi
                    </Button>
                  </div>
                  {selected.sintesi_polizze
                    ? <pre className="whitespace-pre-wrap bg-slate-50 rounded-lg p-3 text-xs text-slate-700">{selected.sintesi_polizze}</pre>
                    : <p className="text-xs text-slate-400">Non ancora verificato (la verifica richiede 1-2 minuti).</p>}
                </div>

                <div className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-indigo-700 uppercase flex items-center gap-1"><Sparkles size={13} /> Proposta AI</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Select value={prodScelto} onValueChange={setProdScelto}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid="prodotto-select"><SelectValue placeholder="L'AI sceglie il migliore" /></SelectTrigger>
                      <SelectContent>
                        {PRODOTTI.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 text-xs" disabled={busy === 'ai'} onClick={() => generaAI(selected.id, prodScelto)} data-testid="genera-ai-btn">
                      {busy === 'ai' ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />} Genera
                    </Button>
                  </div>
                  {selected.prodotto_suggerito && (
                    <div className="mb-2">
                      <Badge className="bg-indigo-600 text-white border-0">{selected.prodotto_suggerito}</Badge>
                      {selected.motivazione_ai && <p className="text-xs text-slate-600 mt-1">{selected.motivazione_ai}</p>}
                    </div>
                  )}
                  {selected.messaggio_wa !== undefined && selected.messaggio_wa !== '' && (
                    <div className="space-y-2">
                      <div>
                        <label className="text-[11px] text-slate-500">Messaggio WhatsApp</label>
                        <Textarea value={selected.messaggio_wa || ''} rows={5}
                          onChange={(e) => setSelected((s) => ({ ...s, messaggio_wa: e.target.value }))}
                          className="text-sm" data-testid="messaggio-wa" />
                      </div>
                      {selected.traccia_chiamata && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-600 font-medium">Traccia per la telefonata</summary>
                          <pre className="whitespace-pre-wrap bg-white rounded p-2 mt-1 border border-slate-100">{selected.traccia_chiamata}</pre>
                        </details>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-xs" disabled={busy === 'wa'}
                          onClick={async () => { await patch(selected.id, { messaggio_wa: selected.messaggio_wa }, true); inviaWA(selected.id); }}
                          data-testid="invia-wa-btn">
                          {busy === 'wa' ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />} Invia WhatsApp
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-500">Esito</label>
                    <Select value={selected.esito} onValueChange={(v) => patch(selected.id, { esito: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid="esito-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ESITI.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 flex items-center gap-1"><CalendarClock size={11} /> Richiamami il</label>
                    <Input type="date" value={(selected.richiamo_at || '').slice(0, 10)} className="h-8 text-xs"
                      onChange={(e) => patch(selected.id, { richiamo_at: e.target.value, esito: 'da_richiamare' })} data-testid="richiamo-date" />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-slate-500">Note interne</label>
                  <Textarea value={selected.note || ''} rows={2} className="text-sm"
                    onChange={(e) => setSelected((s) => ({ ...s, note: e.target.value }))}
                    onBlur={(e) => patch(selected.id, { note: e.target.value }, true)} data-testid="note-input" />
                </div>

                <div className="border border-slate-200 rounded-lg p-3" data-testid="conversazione-box">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1"><MessageCircle size={13} /> Conversazione WhatsApp</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === 'sentiment-ai'} onClick={() => analizzaSentiment(selected.id)} data-testid="analizza-sentiment-btn">
                      {busy === 'sentiment-ai' ? <Loader2 size={13} className="animate-spin mr-1" /> : <Brain size={13} className="mr-1" />} Analizza sentiment
                    </Button>
                  </div>
                  {conv?.sentiment && (
                    <div className="mb-2 text-xs bg-slate-50 rounded-md p-2">
                      <div className="flex items-center gap-2">
                        {SENT[conv.sentiment.interesse] && (
                          <span className={`px-2 py-0.5 rounded-full flex items-center gap-1 ${SENT[conv.sentiment.interesse].c}`}>
                            {React.createElement(SENT[conv.sentiment.interesse].Icon, { size: 11 })} {SENT[conv.sentiment.interesse].l}
                          </span>
                        )}
                      </div>
                      {conv.sentiment.riassunto && <p className="text-slate-600 mt-1">{conv.sentiment.riassunto}</p>}
                    </div>
                  )}
                  {(() => {
                    const msgs = conv?.messaggi || [];
                    if (!msgs.length) return <p className="text-xs text-slate-400">Nessun messaggio ancora. Premi "Analizza sentiment" per leggere la conversazione.</p>;
                    const perMsg = conv?.sentiment?.per_messaggio || {};
                    let ci = 0;
                    return (
                      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                        {msgs.map((m, idx) => {
                          let note = null;
                          if (!m.from_me) { ci += 1; note = perMsg[String(ci)]; }
                          return (
                            <div key={idx} className={`flex ${m.from_me ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${m.from_me ? 'bg-green-100 text-slate-800' : 'bg-slate-100 text-slate-800'}`}>
                                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                                {note && (
                                  <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded border text-[10px] ${SENT_MSG[note.sentiment] || SENT_MSG.neutro}`}>
                                    <Sparkles size={9} /> {note.nota || note.sentiment}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {conv?.sentiment_checked_at && <p className="text-[10px] text-slate-400 mt-2">Ultima analisi: {new Date(conv.sentiment_checked_at).toLocaleString('it-IT')}</p>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <Dialog open={showSondaggio} onOpenChange={setShowSondaggio}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sondaggio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare size={18} /> Sondaggio interesse — invio in blocco</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {campagna?.stato === 'in_corso' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="campagna-progress">
                <div className="flex items-center gap-2 font-medium text-amber-700"><Loader2 size={15} className="animate-spin" /> Campagna in corso…</div>
                <div className="text-xs text-amber-700 mt-1">{campagna.inviati || 0} / {campagna.totale} inviati · {campagna.falliti || 0} falliti</div>
              </div>
            ) : (
              <>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-600">Verrà inviato il messaggio ai clienti della vista corrente <b>"{VISTE.find((v) => v.v === vista)?.l}"</b> (solo chi non è ancora stato contattato).</p>
                  {preview && (
                    <p className="mt-1 font-semibold text-slate-800" data-testid="sondaggio-count">Destinatari: {preview.totale}</p>
                  )}
                  {preview?.esempio && (
                    <div className="mt-2">
                      <div className="text-[11px] text-slate-400">Esempio ({preview.esempio_cliente}):</div>
                      <pre className="whitespace-pre-wrap bg-white border border-slate-100 rounded p-2 text-xs mt-1">{preview.esempio}</pre>
                    </div>
                  )}
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-slate-600">Modifica i testi (persona fisica / azienda)</summary>
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="text-[11px] text-slate-500">Persona fisica — usa {'{nome}'}</label>
                      <Textarea rows={5} value={templates.fisica} onChange={(e) => setTemplates((t) => ({ ...t, fisica: e.target.value }))} data-testid="tpl-fisica" />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500">Azienda (persona giuridica) — usa {'{nome}'}</label>
                      <Textarea rows={5} value={templates.giuridica} onChange={(e) => setTemplates((t) => ({ ...t, giuridica: e.target.value }))} data-testid="tpl-giuridica" />
                    </div>
                    <Button size="sm" variant="outline" onClick={salvaTemplate} disabled={busy === 'tpl'} data-testid="salva-tpl-btn">
                      {busy === 'tpl' ? <Loader2 size={13} className="animate-spin mr-1" /> : null} Salva testi
                    </Button>
                  </div>
                </details>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSondaggio(false)}>Chiudi</Button>
            {campagna?.stato !== 'in_corso' && (
              <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={busy === 'campagna' || !preview?.totale}
                onClick={avviaCampagna} data-testid="avvia-campagna-btn">
                {busy === 'campagna' ? <Loader2 size={15} className="animate-spin mr-1" /> : <Send size={15} className="mr-1" />}
                Avvia invio ({preview?.totale || 0})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
